import { inject, Injectable } from '@angular/core';
import { InternalAuthApi } from '../api/internal-auth-api';
import {
  LoginAuthCodeRequest,
  InternalUserLoginRequest,
  AuthenticatedUser,
  SessionService,
  RefreshSessionRequest,
  CacheService,
  NewPasswordRequest,
} from '@pgaoc/shared-lib';
import { catchError, Observable, of, switchMap, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InternalAuthRepository {
  private internalAuthApi = inject(InternalAuthApi);
  private sessionService = inject(SessionService);
  private cacheService = inject(CacheService);
  private rememberMeEmailCacheKey = 'internalRememberMeEmailCacheKey';

  login(request: InternalUserLoginRequest): Observable<AuthenticatedUser> {
    return this.internalAuthApi.login(request);
  }

  resetPassword(email: string): Observable<void> {
    return this.internalAuthApi.resetPassword(email);
  }

  submitMFACode(request: LoginAuthCodeRequest): Observable<AuthenticatedUser> {
    return this.internalAuthApi.submitMFACode(request);
  }

  setNewPassword(email: string, newPasswordRequest: NewPasswordRequest): Observable<void> {
    return this.internalAuthApi.setNewPassword(email, newPasswordRequest);
  }

  isAuthenticated(): Observable<AuthenticatedUser | null> {
    return this.sessionService.authenticatedUser$.pipe(
      take(1),
      switchMap(user => {
        const cachedSession = this.sessionService.getCachedAuthenticatedUser();
        if (!user && !!cachedSession) {
          user = cachedSession;
          this.sessionService.setAuthenticatedUser(user);
        }
        if (user?.userSession && !!user?.userSession?.accessToken) {
          return of(user);
        }
        return this.refreshSession();
      }),
    );
  }

  refreshSession(): Observable<AuthenticatedUser | null> {
    return this.sessionService.authenticatedUser$.pipe(
      take(1),
      switchMap(currentUser => {
        if (currentUser?.userSession?.refreshToken) {
          this.sessionService.setRefreshingSession(true);
          const req: RefreshSessionRequest = {
            refreshToken: currentUser.userSession.refreshToken,
          };
          return this.internalAuthApi.refreshSession(req, currentUser).pipe(
            tap(user => {
              this.sessionService.setAuthenticatedUser(user);
              this.sessionService.setRefreshingSession(false);
            }),
          );
        } else {
          this.sessionService.setRefreshingSession(false);
          return of(null);
        }
      }),
      catchError(error => {
        this.sessionService.setRefreshingSession(false);
        if (error.status === 400) {
          this.sessionService.destroySession.next(true);
        }
        // Pass other errors along
        throw error;
      }),
    );
  }

  rememberLoginEmail(email: string) {
    this.cacheService.storeData(this.rememberMeEmailCacheKey, email);
  }

  getRememberedLoginEmail(): string | null {
    return this.cacheService.loadData<string>(this.rememberMeEmailCacheKey);
  }
}
